/**
 * @owner @Appboy/operator
 * @fileoverview GET handler for the "/dust/auth/:app_group_id" endpoint
 */

import type { DustConfigResponse } from "@lib/dust";
import fetch, { encodeParams } from "@lib/fetch";

import { reactQueryFetcherAdapter } from "../reactQueryFetcherAdapters";
import type { RootCacheKey } from "../types";
import { cacheKey } from "./constants";

type GetDustConfigArgs = [appGroupId: string] | [appGroupId: string, tabIdentifier: string];

export const getDustConfig = reactQueryFetcherAdapter<
  typeof cacheKey,
  readonly [rootKey: RootCacheKey<typeof cacheKey>, ...originalKey: GetDustConfigArgs],
  GetDustConfigArgs,
  DustConfigResponse
>(
  cacheKey,
  (signal: AbortSignal, ...[appGroupId, tabIdentifier]: GetDustConfigArgs): Promise<DustConfigResponse> => {
    // The deployed SharedWorker bundle hash (injected into the page config at
    // load time) is salted into the MITE backend-side, so shipping new worker
    // code hands freshly-loaded tabs a fresh DUST session. Omitted when the hash
    // is unavailable (e.g. local dev before the bundle is built), in which case
    // the backend falls back to the legacy MITE.
    const sharedWorkerHash = window.brazeDashboardConfig?.dustSharedWorkerHash;
    // tabIdentifier is supplied only by the Operator direct-mode connection. It is
    // salted into the derived session id backend-side so each tab gets a distinct,
    // session-targeted MITE. The shared-worker connection omits it so all tabs keep
    // sharing one MITE.
    const queryParams: Record<string, string> = {};
    if (sharedWorkerHash) {
      queryParams.version = sharedWorkerHash;
    }
    if (tabIdentifier) {
      queryParams.tabIdentifier = tabIdentifier;
    }
    const query = Object.keys(queryParams).length > 0 ? `?${encodeParams(queryParams)}` : "";
    const url = `/dust/auth/${appGroupId}${query}`;
    return fetch(url, "GET", undefined, { signal });
  }
);
